export const STOCK_COVERAGE_PERIOD_MONTHS = 12;

export interface StockCoverageRow {
  category: string;
  soldQty: number;
  salesAmount: number;
  monthlyAvgQty: number;
  stockQty: number;
  stockValue: number;
  // null: dönemde satış yok, stok süresi hesaplanamıyor
  monthsOfStock: number | null;
}

interface ProductLike {
  id: string;
  category: string | null;
  quantity_on_hand: number | null;
  current_cost: number | null;
  initial_cost: number | null;
}

interface SaleItemLike {
  product_id: string | null;
  quantity: number | null;
  line_total_net: number | null;
  products?: { category: string | null } | null;
}

interface SaleLike {
  sale_items?: Array<SaleItemLike | null> | null;
}

export interface CalculateStockCoverageInput {
  products: ProductLike[];
  sales: SaleLike[];
  periodMonths?: number;
}

export interface StockCoverageReport {
  rows: StockCoverageRow[];
  total: StockCoverageRow;
}

function buildRow(
  category: string,
  soldQty: number,
  salesAmount: number,
  stockQty: number,
  stockValue: number,
  periodMonths: number,
): StockCoverageRow {
  const monthlyAvgQty = soldQty > 0 ? soldQty / periodMonths : 0;

  return {
    category,
    soldQty,
    salesAmount: Math.round(salesAmount),
    monthlyAvgQty,
    stockQty,
    stockValue: Math.round(stockValue),
    monthsOfStock: monthlyAvgQty > 0 ? stockQty / monthlyAvgQty : null,
  };
}

export function calculateStockCoverageByCategory({
  products,
  sales,
  periodMonths = STOCK_COVERAGE_PERIOD_MONTHS,
}: CalculateStockCoverageInput): StockCoverageReport {
  const categoryMap = new Map<
    string,
    { soldQty: number; salesAmount: number; stockQty: number; stockValue: number }
  >();
  const getCategoryData = (category: string) => {
    let data = categoryMap.get(category);
    if (!data) {
      data = { soldQty: 0, salesAmount: 0, stockQty: 0, stockValue: 0 };
      categoryMap.set(category, data);
    }
    return data;
  };

  const categoryByProductId = new Map<string, string>();

  products.forEach((product) => {
    const category = product.category || 'Kategorisiz';
    categoryByProductId.set(product.id, category);

    const quantity = Number(product.quantity_on_hand ?? 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const unitCost = Number(product.current_cost || product.initial_cost || 0);
    const data = getCategoryData(category);
    data.stockQty += quantity;
    data.stockValue += quantity * (Number.isFinite(unitCost) ? unitCost : 0);
  });

  sales.forEach((sale) => {
    sale.sale_items?.forEach((item) => {
      if (!item) return;

      // Prefer the current product record so sales and stock land in the same category.
      const knownCategory = item.product_id ? categoryByProductId.get(item.product_id) : undefined;
      if (!knownCategory && !item.products) return;
      const category = knownCategory ?? (item.products?.category || 'Kategorisiz');

      const data = getCategoryData(category);
      data.soldQty += Number(item.quantity ?? 0) || 0;
      data.salesAmount += Number(item.line_total_net ?? 0) || 0;
    });
  });

  const rows = Array.from(categoryMap.entries())
    .filter(([, data]) => data.stockQty > 0 || data.soldQty > 0)
    .map(([category, data]) =>
      buildRow(category, data.soldQty, data.salesAmount, data.stockQty, data.stockValue, periodMonths),
    )
    .sort((a, b) => {
      // Longest coverage first; categories with stock but no sales lead the list.
      const aMonths = a.monthsOfStock ?? Number.POSITIVE_INFINITY;
      const bMonths = b.monthsOfStock ?? Number.POSITIVE_INFINITY;
      if (aMonths !== bMonths) return bMonths - aMonths;
      return b.stockValue - a.stockValue || a.category.localeCompare(b.category, 'tr-TR');
    });

  const total = buildRow(
    'Toplam',
    rows.reduce((sum, row) => sum + row.soldQty, 0),
    rows.reduce((sum, row) => sum + row.salesAmount, 0),
    rows.reduce((sum, row) => sum + row.stockQty, 0),
    rows.reduce((sum, row) => sum + row.stockValue, 0),
    periodMonths,
  );

  return { rows, total };
}

export function formatMonthsOfStock(monthsOfStock: number | null): string {
  if (monthsOfStock === null) return 'Satış yok';
  return `${monthsOfStock.toLocaleString('tr-TR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} ay`;
}
